import { useStore } from '../store/useStore';
import { TrendingUp, Award, Flame } from 'lucide-react';
import { formatCurrency } from '../lib/format';

const RANK_COLORS = ['#FFD700', '#C0C0C0', '#CD7F32'];

export function TeamLeaderboard() {
  const { team, leads, memberStreaks, loginStreak, taskStreak, currentUser } = useStore();

  const leaderData = (team || []).map(member => {
    const assignedLeads = (leads || []).filter(l => l && (l.assignedTo === member.id || l.assignedTo === member.name));
    const closedWon = assignedLeads.filter(l => l.status === 'closed-won');
    const totalRevenue = closedWon.reduce((sum, l) => sum + (l.estimatedValue || 0), 0);
    const totalProfit = closedWon.reduce((sum, l) => {
      // Profit = estimatedValue - offerAmount (wholesale spread)
      const profit = l.offerAmount && l.estimatedValue
        ? Math.max(0, l.estimatedValue - l.offerAmount)
        : (l.estimatedValue || 0) * 0.2; // Fallback: 20% margin estimate
      return sum + profit;
    }, 0);
    const conversionRate = assignedLeads.length > 0
      ? Math.round((closedWon.length / assignedLeads.length) * 100)
      : 0;

    const isCurrentUser = member.id === currentUser?.id || member.email === currentUser?.email;
    const streaks = memberStreaks || {};
    const lStreak = streaks[member.id]?.login ?? (isCurrentUser ? loginStreak : 0);
    const tStreak = streaks[member.id]?.task ?? (isCurrentUser ? taskStreak : 0);

    return {
      ...member,
      closedWonCount: closedWon.length,
      totalRevenue,
      totalProfit,
      conversionRate,
      assignedCount: assignedLeads.length,
      loginStreak: lStreak,
      taskStreak: tStreak,
    };
  }).sort((a, b) => (b.totalRevenue || 0) - (a.totalRevenue || 0));

  const maxRevenue = leaderData[0]?.totalRevenue || 1;

  return (
    <div className="liquid-glass border border-[var(--t-border-subtle)] rounded-2xl overflow-hidden shadow-sm">
      {/* Header */}
      <div className="px-4 py-3 border-b border-[var(--t-border)] flex items-center justify-between"
           style={{ background: 'var(--t-surface-hover)' }}>
        <div className="flex items-center gap-2">
          <Award className="w-4 h-4" style={{ color: 'var(--t-primary)' }} />
          <h3 className="font-bold text-sm text-white uppercase tracking-tight">Top Performance</h3>
        </div>
        <div className="flex items-center gap-1 text-[9px] uppercase tracking-wider font-black" style={{ color: 'var(--t-text-muted)' }}>
          <TrendingUp size={10} /> Monthly
        </div>
      </div>

      {/* Leader cards */}
      <div className="divide-y divide-[var(--t-border)]">
        {leaderData.map((member, idx) => (
          <div key={member.id} className="px-4 py-5 group hover:bg-[var(--t-surface-hover)] transition-all">
            <div className="flex items-start gap-3">
              {/* Rank + Avatar */}
              <div className="relative shrink-0">
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center text-sm font-black shadow-lg"
                  style={{ background: 'var(--t-primary-dim)', color: 'var(--t-primary)', border: '1px solid var(--t-primary-dim)' }}
                >
                  {(member.full_name || member.name || '?').charAt(0)}
                </div>
                {idx < 3 && (
                  <div
                    className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full flex items-center justify-center border-2 border-[var(--t-surface)] shadow-md"
                    style={{ background: RANK_COLORS[idx] }}
                  >
                    <span className="text-[9px] font-black text-black">{idx + 1}</span>
                  </div>
                )}
              </div>

              {/* Name + Stats */}
              <div className="min-w-0 flex-1">
                <div className="flex flex-col gap-0.5 mb-3">
                  <div className="flex items-center justify-between">
                    <p className="text-[12px] font-black text-white uppercase tracking-tight italic truncate">
                      {member.full_name || member.name || 'Unknown'}
                    </p>
                    {member.loginStreak > 0 && (
                      <div className="flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-orange-500/10 text-orange-400 text-[8px] font-black border border-orange-500/20 shrink-0">
                        <Flame size={8} /> {member.loginStreak}d
                      </div>
                    )}
                  </div>
                </div>

                {/* Metrics Grid */}
                <div className="grid grid-cols-2 gap-y-3 gap-x-2 mb-3">
                  <div>
                    <p className="text-[8px] font-black uppercase tracking-widest text-[var(--t-text-muted)] mb-0.5">Revenue</p>
                    <div className="flex items-center gap-1">
                      <span className="text-[11px] font-black text-[var(--t-success)] truncate">
                        {formatCurrency(member.totalRevenue)}
                      </span>
                    </div>
                  </div>
                  <div>
                    <p className="text-[8px] font-black uppercase tracking-widest text-[var(--t-text-muted)] mb-0.5">Win Rate</p>
                    <div className="flex items-center gap-1">
                      <span className="text-[11px] font-black text-white">{member.conversionRate}%</span>
                    </div>
                  </div>
                  <div>
                    <p className="text-[8px] font-black uppercase tracking-widest text-[var(--t-text-muted)] mb-0.5">Profit</p>
                    <div className="flex items-center gap-1">
                      <span className="text-[11px] font-black text-emerald-400 truncate">
                        {formatCurrency(member.totalProfit)}
                      </span>
                    </div>
                  </div>
                  <div>
                    <p className="text-[8px] font-black uppercase tracking-widest text-[var(--t-text-muted)] mb-0.5">Won</p>
                    <div className="flex items-center gap-1">
                      <span className="text-[11px] font-black text-white">{member.closedWonCount}</span>
                    </div>
                  </div>
                </div>

                {/* Progress bar */}
                <div className="relative pt-1">
                  <div className="w-full h-1.5 bg-[var(--t-background)] rounded-full overflow-hidden border border-white/5">
                    <div
                      className="h-full rounded-full transition-all duration-700 ease-out shadow-[0_0_10px_var(--t-primary)]"
                      style={{
                        width: `${Math.min(100, (member.totalRevenue / maxRevenue) * 100)}%`,
                        background: 'linear-gradient(90deg, var(--t-primary), #be83fa)',
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
        {leaderData.length === 0 && (
          <div className="px-4 py-12 text-center text-[var(--t-text-muted)] text-sm font-black uppercase tracking-widest opacity-50">
            No active operatives detected.
          </div>
        )}
      </div>
    </div>
  );
}
